import { IonIcon, IonModal } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { Content } from "@/components/content";
import { xMarkIcon } from "@/lib/icons/@heroicons/react/20/solid";

interface PermissionExplainerModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

export function PermissionExplainerModal({
  isOpen,
  onConfirm,
  onDismiss,
}: PermissionExplainerModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);

  const handleConfirm = () => {
    modalRef.current?.dismiss();
    onConfirm();
  };

  const handleDismiss = () => {
    modalRef.current?.dismiss();
    onDismiss();
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      className="permission-explainer-modal"
    >
      <Content inModal>
        <div className="flex h-full flex-col gap-6 p-6">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="self-end rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <IonIcon icon={xMarkIcon} className="size-6 text-gray-600 dark:text-gray-400" />
          </button>

          {/* Hero Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="bg-brand-400/20 absolute inset-0 animate-pulse rounded-full blur-xl" />
              <div className="dark:bg-brand-900/30 relative flex size-24 items-center justify-center rounded-full bg-brand-100">
                <svg
                  className="size-12 text-brand-600 dark:text-brand-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {/* Sparkle effect */}
                <svg
                  className="absolute -right-1 -top-1 size-6 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-txt-primary">
              {t("settings.notifications.permission-explainer.title")}
            </h2>
            <p className="mt-2 text-base text-txt-secondary">
              {t("settings.notifications.permission-explainer.subtitle")}
            </p>
          </div>

          {/* Benefits List */}
          <div className="flex flex-col gap-4">
            <BenefitItem
              icon={
                <svg className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              }
              title={t("settings.notifications.permission-explainer.benefits.assignments")}
            />
            <BenefitItem
              icon={
                <svg className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              }
              title={t("settings.notifications.permission-explainer.benefits.changes")}
            />
            <BenefitItem
              icon={
                <svg className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                  />
                </svg>
              }
              title={t("settings.notifications.permission-explainer.benefits.updates")}
            />
          </div>

          {/* Buttons */}
          <div className="mt-auto flex flex-col gap-3">
            <Button
              expand="block"
              onClick={handleConfirm}
              className="h-12 part-native:text-base part-native:font-bold"
            >
              {t("settings.notifications.permission-explainer.actions.enable")}
            </Button>
            <Button
              expand="block"
              fill="clear"
              onClick={handleDismiss}
              className="h-12 text-txt-secondary part-native:text-base"
            >
              {t("settings.notifications.permission-explainer.actions.not-now")}
            </Button>
          </div>
        </div>
      </Content>
    </IonModal>
  );
}

interface BenefitItemProps {
  icon: React.ReactNode;
  title: string;
}

function BenefitItem({ icon, title }: BenefitItemProps) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
      <div className="dark:bg-brand-900/50 flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:text-brand-400">
        {icon}
      </div>
      <p className="pt-2 text-base text-txt-primary">{title}</p>
    </div>
  );
}
